import { useRequest } from 'features/request/useRequest';
import type { BimChapterEnum, BimPriceLevelEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { prepareCodeToSort } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/hooks/helpers';
import { pickTrue } from 'utils';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { bimWorkOrderChangePriceGql } from '../bimWorkOrderChangePriceQueries';

const getWhereVariables = (fieldName: string, checkedValues: string[], prevState?: Record<string, any>) => {
    const [firstPart, secondPart] = fieldName.split('.');

    if (!checkedValues.length) {
        if (secondPart && prevState?.and[firstPart][secondPart]) {
            const value = pickTrue<any, any>({
                ...prevState?.and[firstPart],
                [secondPart]: undefined,
            });

            const hasActive = Object.values(value).some((v) => !!v);

            return {
                [firstPart]: hasActive ? value : undefined,
            };
        }

        if (!secondPart && prevState?.and[fieldName]) {
            return {
                [fieldName]: undefined,
            };
        }
    }

    if (secondPart) {
        return {
            [firstPart]: {
                ...prevState?.and[firstPart],
                [secondPart]: { in: checkedValues },
            },
        };
    }

    return { [fieldName]: { in: checkedValues } };
};

export const useBimWorkOrderChangePriceData = ({
    workOrderChangeId,
    workOrderRootObjectId,
    workOrderChangeRootObjectId = null,
    chapter,
    includeRows = false,
    priceLevel,
}: {
    chapter: BimChapterEnum | undefined;
    workOrderChangeId: number;
    workOrderRootObjectId: number | undefined;
    workOrderChangeRootObjectId: number | null | undefined;
    includeRows?: boolean;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const [filtersWhere, setFiltersWhere] = useState<Record<string, any> | undefined>(undefined);

    const variables = useMemo(() => {
        if (chapter && workOrderChangeId) {
            return {
                workOrderChangeId,
                workOrderRootObjectId,
                workOrderChangeRootObjectId,
                chapter,
                includeRows,
                priceLevel,
                where: filtersWhere,
            };
        }

        return undefined;
    }, [
        chapter,
        filtersWhere,
        includeRows,
        priceLevel,
        workOrderChangeId,
        workOrderChangeRootObjectId,
        workOrderRootObjectId,
    ]);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getWhereVariables(fieldName, checkedValues, prevValues),
            },
        }));
    }, []);

    const [data, , , request] = useRequest({
        requestId: 'bimWorkOrderChangePrice',
        query: bimWorkOrderChangePriceGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'bimWorkOrderChangePrice',
        requestFn: request,
        requestVariables: variables,
    });

    useEffect(() => {
        if (data?.bimWorkOrderChangePrices?.items && getRowParams?.current) {
            const sortedItems = R.sort(
                (a, b) =>
                    prepareCodeToSort(a?.completedValue?.code ?? '') - prepareCodeToSort(b?.completedValue?.code ?? ''),
                data.bimWorkOrderChangePrices.items,
            ).map((item) => ({
                ...item,
                id:
                    item.workOrderChangePriceId ?? item.workOrderPriceId
                        ? `${item.workOrderChangePriceId}_${item.workOrderPriceId}`
                        : Math.random().toString(),
            }));

            getRowParams?.current?.successCallback(sortedItems, data.bimWorkOrderChangePrices?.totalCount);
        }
    }, [data?.bimWorkOrderChangePrices?.items, data?.bimWorkOrderChangePrices?.totalCount, getRowParams]);

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `${chapter}_${workOrderRootObjectId}_${workOrderChangeRootObjectId}`,
                    getDefaultFilterCleaner({
                        changeFilterHandler,
                        setFiltersWhere,
                    }),
                ),
            }),
            [changeFilterHandler, chapter, workOrderChangeRootObjectId, workOrderRootObjectId],
        ),
    );

    const totalItems = useMemo(
        () => data?.bimWorkOrderChangePrices?.totalCount,
        [data?.bimWorkOrderChangePrices?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    return { dataSource, totalItems, fetchedItems, changeFilterHandler };
};
